import React, { useState } from 'react';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Button } from './ui/button';
import { Calendar as CalendarIcon } from 'lucide-react';
import { cn } from '../lib/utils';
import { pl } from 'date-fns/locale';

// Format date as DD.MM.YYYY (Polish standard)
export const formatDatePL = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

// Convert YYYY-MM-DD string to Date, or pass Date through
const toDate = (value) => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  const d = new Date(value + 'T00:00:00');
  return isNaN(d.getTime()) ? undefined : d;
};

// Convert Date to YYYY-MM-DD string for storage
export const toISODate = (date) => {
  if (!date) return '';
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return '';
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const DatePickerField = ({
  value,
  onChange,
  placeholder = 'Wybierz datę',
  className,
  disabled = false,
  'data-testid': testId,
}) => {
  const [open, setOpen] = useState(false);
  const selected = toDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            'w-full justify-start text-left font-normal bg-[var(--crm-bg-panel)] border-[var(--crm-border)] hover:bg-[var(--crm-bg-main)] hover:text-[var(--crm-text-primary)] h-10',
            !selected && 'text-[var(--crm-text-muted)]',
            selected && 'text-[var(--crm-text-primary)]',
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-[var(--crm-text-secondary)] shrink-0" />
          {selected ? formatDatePL(selected) : <span className="text-[var(--crm-text-muted)]">{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[var(--crm-bg-main)] border-[var(--crm-border)]" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(date) => {
            onChange(date ? toISODate(date) : '');
            setOpen(false);
          }}
          locale={pl}
          className="rounded-md"
          classNames={{
            months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
            month: "space-y-4",
            caption: "flex justify-center pt-1 relative items-center",
            caption_label: "text-sm font-medium text-[var(--crm-text-primary)]",
            nav: "space-x-1 flex items-center",
            nav_button: "h-9 w-9 bg-transparent p-0 opacity-60 hover:opacity-100 border border-[var(--crm-border)] rounded-md inline-flex items-center justify-center text-[var(--crm-text-primary)] hover:bg-[var(--crm-bg-btn-secondary)]",
            nav_button_previous: "absolute left-1",
            nav_button_next: "absolute right-1",
            table: "w-full border-collapse space-y-1",
            head_row: "flex",
            head_cell: "text-[var(--crm-text-secondary)] rounded-md w-10 font-normal text-[0.8rem]",
            row: "flex w-full mt-2",
            cell: "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-[#FF6B35]/20 [&:has([aria-selected])]:rounded-md",
            day: "h-10 w-10 p-0 font-normal text-[var(--crm-text-secondary)] hover:bg-[var(--crm-bg-btn-secondary)] hover:text-[var(--crm-text-primary)] rounded-md inline-flex items-center justify-center aria-selected:opacity-100 transition-colors",
            day_selected: "bg-[#FF6B35] text-white hover:bg-[#FF6B35] hover:text-white focus:bg-[#FF6B35] focus:text-white",
            day_today: "bg-[var(--crm-bg-btn-secondary)] text-[var(--crm-text-primary)] font-bold",
            day_outside: "text-slate-600 opacity-50",
            day_disabled: "text-slate-600 opacity-30",
            day_hidden: "invisible",
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DatePickerField;
